import { Link, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Download, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui";
import { useInstall } from "@/lib/pwa";
import { useAppConfig } from "@/lib/queries";
import type { MeDTO } from "@shared/types";
import { LegalLinks } from "@/components/Legal";
import { PasskeySignIn } from "@/components/PasskeySignIn";
import { PairedKids } from "./PairedKids";

const pillars = [
  {
    title: "Obligations first",
    text: "Responsibilities unlock paid jobs. Kids can see the reward waiting, but they clear their duties first.",
  },
  {
    title: "Entrepreneurship from day one",
    text: "Kids pitch their own jobs and negotiate a price. You approve before anything goes live.",
  },
  {
    title: "Rewards and consequences",
    text: "Cash, screen time, or a goal they're saving for. Penalties are fair and kids can earn their way back.",
  },
  {
    title: "Private by design",
    text: "Built for kids under COPPA: parents consent and stay in control. No ads, no tracking, and no AI in the app.",
  },
];

export function Landing() {
  const install = useInstall();
  const marketingUrl = useAppConfig().data?.marketingUrl;
  const qc = useQueryClient();
  const navigate = useNavigate();

  function signedIn(me: MeDTO) {
    qc.setQueryData(["me"], me);
    navigate(me.user.role === "kid" ? "/kid" : me.family.onboarded ? "/parent" : "/parent/setup", { replace: true });
  }

  return (
    <div className="min-h-dvh bg-canvas">
      <div className="mx-auto flex min-h-dvh max-w-xl flex-col px-5 pb-[calc(env(safe-area-inset-bottom)+24px)] pt-[calc(env(safe-area-inset-top)+28px)]">
        <div className="flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-10" />
          <span className="text-lg font-black tracking-tight">Meaningful Rewards</span>
        </div>

        <section className="mt-10">
          <h1 className="text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl">
            Handle your obligations first.
            <span className="block text-brand">Then go earn.</span>
          </h1>
          <p className="mt-4 text-lg text-muted">
            A household economy that teaches kids initiative, entrepreneurship, and accountability, with rewards picked
            for each kid.
          </p>
          {marketingUrl && (
            <a
              href={marketingUrl}
              className="mt-3 inline-flex items-center gap-1 font-bold text-brand hover:underline"
              target="_blank"
              rel="noopener"
            >
              See how it works <ArrowUpRight className="size-4" />
            </a>
          )}
        </section>

        <PairedKids className="mt-8" />

        <div className="mt-8 grid gap-3">
          <Link to="/signup">
            <Button size="lg" block>
              Create your family
            </Button>
          </Link>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/login">
              <Button size="lg" variant="secondary" block>
                Parent sign in
              </Button>
            </Link>
            <Link to="/kid-login">
              <Button size="lg" variant="coin" block>
                I'm a kid 👋
              </Button>
            </Link>
          </div>
          <PasskeySignIn onDone={signedIn} />
        </div>

        <ol className="mt-10 divide-y divide-line border-y border-line">
          {pillars.map((p, i) => (
            <li key={p.title} className="flex gap-4 py-4">
              <span className="text-2xl font-black leading-none text-brand tabular-nums">{i + 1}</span>
              <div>
                <h2 className="font-extrabold">{p.title}</h2>
                <p className="mt-1 text-sm text-muted">{p.text}</p>
              </div>
            </li>
          ))}
        </ol>

        {!install.installed && (
          <section className="mt-8 rounded-2xl bg-sunken p-4 text-sm">
            <div className="flex items-center gap-2 font-extrabold">
              <Download className="size-4" /> Install it like an app
            </div>
            {install.canPrompt ? (
              <Button size="sm" variant="secondary" className="mt-3" onClick={() => install.prompt()}>
                Install Meaningful Rewards
              </Button>
            ) : install.ios ? (
              <p className="mt-1 text-muted">
                On iPhone or iPad: tap the <b>Share</b> button, then <b>Add to Home Screen</b>.
              </p>
            ) : (
              <p className="mt-1 text-muted">
                On Android, tablets and computers: use your browser menu and choose <b>Install app</b> or{" "}
                <b>Add to Home screen</b>.
              </p>
            )}
          </section>
        )}

        <footer className="mt-auto space-y-2 pt-10 text-center text-xs text-faint">
          <p>Made for families. Your data stays in your family's account.</p>
          <LegalLinks />
        </footer>
      </div>
    </div>
  );
}
